import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// The app answers on master-splitter.com now, but the old host still serves
// the same build, because a PWA's scope is pinned to the origin it was
// installed from and a redirect would not move it either. This banner is how
// those users find out to come over before the old host is switched off.
//
// SHUTDOWN_DATE is a promise made to users, in their own language, on screen.
// Moving it later is fine; letting it pass while the host is still up is not,
// and neither is taking the host down early — anyone who has not migrated by
// then loses access, and an installed PWA cannot be recovered by them without
// reinstalling from the new origin.
const LEGACY_HOST = 'mastersplitter.hayotush.com';
const NEW_ORIGIN = 'https://master-splitter.com';
const SHUTDOWN_DATE = '2026-09-18';
const SNOOZE_KEY = 'legacyDomainNoticeSnoozedUntil';
const SNOOZE_DAYS = 7;
// Inside this window the notice stops being dismissible. A one-off dismissal
// that lasted forever would quietly strand exactly the people it is meant to
// warn, so "later" buys a week, and stops being offered once a week is all
// that is left.
const FINAL_WEEK_DAYS = 7;
const DAY_MS = 86400000;

export default function LegacyDomainNotice() {
    // ponytail: hostname check, no feature flag. The whole component gets
    // deleted the day the legacy host is retired.
    const onLegacyHost = window.location.hostname === LEGACY_HOST;
    const [snoozedUntil, setSnoozedUntil] = useState(
        () => Number(localStorage.getItem(SNOOZE_KEY)) || 0
    );
    const { t, i18n } = useTranslation('common');

    const now = Date.now();
    const daysLeft = Math.max(0, Math.ceil((Date.parse(SHUTDOWN_DATE) - now) / DAY_MS));
    const isFinalWeek = daysLeft <= FINAL_WEEK_DAYS;

    if (!onLegacyHost) return null;
    if (!isFinalWeek && now < snoozedUntil) return null;

    // Someone running the installed app needs a different instruction: opening
    // the new address in a browser does not replace the icon already on their
    // home screen, which stays bound to this origin. They have to install again.
    const isInstalled =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;

    const deadline = new Date(SHUTDOWN_DATE).toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'long',
    });

    const snooze = () => {
        const until = now + SNOOZE_DAYS * DAY_MS;
        localStorage.setItem(SNOOZE_KEY, String(until));
        setSnoozedUntil(until);
    };

    return (
        <div
            className={'legacy-domain-notice' + (isFinalWeek ? ' legacy-domain-notice--urgent' : '')}
            role={isFinalWeek ? 'alert' : 'status'}
        >
            <div className="legacy-domain-notice__text">
                <strong>{t('legacyDomainTitle')}</strong>
                <span>
                    {/* No `count` here on purpose: i18next reads it as a plural
                        selector and would go looking for _one/_other variants
                        of a key that has none. */}
                    {t(isInstalled ? 'legacyDomainBodyInstalled' : 'legacyDomainBody', {
                        date: deadline,
                    })}
                </span>
            </div>
            <div className="legacy-domain-notice__actions">
                {/* Carries the current path across so someone sitting on
                    /expenses lands on /expenses, not the home screen. */}
                <a
                    className="btn btn-ms-primary btn-sm"
                    href={NEW_ORIGIN + window.location.pathname + window.location.search}
                >
                    {t(isInstalled ? 'legacyDomainCtaInstalled' : 'legacyDomainCta')}
                </a>
                {!isFinalWeek && (
                    <button type="button" className="btn btn-sm legacy-domain-notice__later" onClick={snooze}>
                        {t('legacyDomainLater')}
                    </button>
                )}
            </div>
        </div>
    );
}
